import React from 'react';
import { connect } from 'react-redux';
import classnames from 'classnames';
import _kebabCase from 'lodash/kebabCase';
import _uniqueId from 'lodash/uniqueId';
import * as PropTypes from 'prop-types';

import ZpButton from 'components/zpButton';
import { withTracking } from 'hooks';

import { isValidExtendedLocation } from '../../utils';
import { logEventGTM } from '../../utils/analyticsGTM';
import { TRACKING } from '../../utils/constant';
import AutosuggestInput from '../autosuggestInput';

import sitemapClasses from '../../css/sitemap.module.css';
import stylesSearchBar from './searchBar.module.css';
/**
 * COMPONENT_CACHE_KEY is for storing user's entered data from store to localstorage
 */
const COMPONENT_CACHE_KEY = 'siteSearchMap';

const propTypes = {
  title: PropTypes.string,
  searchType: PropTypes.string,
  description: PropTypes.string,
  img: PropTypes.string,
  links: PropTypes.element,
  noLinks: PropTypes.bool,
  user: PropTypes.object,
  criteria: PropTypes.object,
  inputFieldClasses: PropTypes.string,
};

const defaultProps = {
  title: '',
  searchType: 'jobs',
  description: '',
  img: '',
  inputFieldClasses: '',
};

const defaultState = {
  COMPONENT_CACHE_KEY,
  inputList: {
    jobTitle: {
      displayValue: '',
    },
    location: {
      displayValue: '',
    },
    major: {
      displayValue: '',
    },
  },
  normalizedZipCode: null,
  errorMessage: '',
  isSearching: false,
};

class JobSearch extends React.Component {
  constructor(props) {
    super(props);
    this.state = defaultState;
  }

  componentDidMount() {
    this.getStateFromCache();
  }

  getStateFromCache = () => {
    let state = null;
    try {
      state = JSON.parse(localStorage.getItem(COMPONENT_CACHE_KEY));

      if (state && state.COMPONENT_CACHE_KEY === COMPONENT_CACHE_KEY) {
        const {
          inputList: { jobTitle, location },
        } = state;

        this.setNewAutoSuggestValue(this.jobTitleRef, jobTitle.displayValue);
        this.setNewAutoSuggestValue(this.locationRef, location.displayValue);
      }
    } catch (e) {
      console.error('e', e);
    }
  };

  setNewAutoSuggestValue = (ref, value = '') => {
    if (ref && value && ref._typeahead) {
      ref._typeahead.setState({ text: value });
      ref.prefillInput(value);
    } else {
      ref._typeahead.focus();
    }
  };

  setStateToCache = () => {
    const { isSearching } = this.state;
    if (!isSearching) {
      localStorage.setItem(COMPONENT_CACHE_KEY, JSON.stringify(this.state));
    }
  };

  handleSearching = (isSearching) => {
    this.setState({ isSearching });
  };

  /**
   * function in case user enter zip code in location input
   * set first suggestion to location input value
   *
   * @param {Object} data object from onNormalizeData event
   */
  handleNormalizeZipCode = (data) => {
    const locationInputFormat = {
      category: 'location',
      className: 'form-control',
      displayValue: data.display_name,
      errorMessage: "Sorry, we can't find that location. Please try another.",
      id: 'searchLocationCareerTopLevelPage',
      isListOpen: false,
      name: 'location',
      placeholder: 'Location',
      suggestionSelected: true,
      value: data.name,
      valueObj: data.value,
    };
    this.setState({
      normalizedZipCode: locationInputFormat,
    });
  };

  handleChange = (inputElement) => {
    this.setState(
      (prevState) => ({
        inputList: {
          ...prevState.inputList,
          [inputElement.name]: {
            ...prevState.inputList[inputElement.name],
            ...inputElement,
          },
        },
        error: false,
      }),
      () => {
        if (inputElement.suggestionSelected) {
          this.search();
        }
      }
    );
  };

  handleNormalize = (key, data) => {
    this.setState((prevState) => ({
      inputList: {
        ...prevState.inputList,
        [key]: {
          ...prevState.inputList[key],
          normalize: data,
        },
      },
    }));
  };

  // handle ENTER key down
  handleKeyDown = (e) => {
    if (e.which === 13) {
      this.search(e);
    }
  };

  handleError = ({ errorMessage }) => {
    this.setState({
      errorMessage,
    });
  };

  search = (e) => {
    // test()
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    this.setState({
      error: true,
    });
    // generate url if no error
    //if (!this.getCurrentError()) {
    this.generateUrlFromData();
    // log Activity
    this.logActivityForSearch();
    //}
  };

  // get the url from selected data
  generateUrlFromData = () => {
    let { url } = this.checkUrl();

    if (url) {
      this.redirectToNewUrl(url);
    } else {
      this.setState({ error: true });
    }
  };
  logActivityForSearch() {
    const {
      inputList: { jobTitle, location },
    } = this.state;
    const { origin, tracking } = this.props;
    const logPayload = {
      pageCategory: window.pageCategory,
      origin,
    };

    logPayload.title = {
      normalizedTitle: jobTitle.normalize ? jobTitle.normalize.name : jobTitle.value,
      ...(jobTitle.value && { originalSearch: jobTitle.value }),
    };
    logPayload.titleResultFound = !!jobTitle.valueObj;
    logPayload.location = location.displayValue || '';
    logPayload.locationResultFound = !!location.value;

    tracking.logActivity(logPayload, TRACKING.LOG_ACTIVITY.HEADER_SEARCH);

    // push event to GTM and raise upload activity
    logEventGTM({
      event: 'performSearchHeaderBar',
      label: this.checkUrl().label,
    });
  }

  checkUrl = () => {
    const { inputList } = this.state;
    const { originLabel } = this.props;
    let payload = {};
    let url = '';
    let label = originLabel || '';
    let mymatchesRedirect = false;
    let remotePage = false;
    const result = {
      location: false,
      jobTitle: false,
    };
    payload = {
      jobTitle: inputList.jobTitle.valueObj || {},
      location: inputList.location ? inputList.location.value : '',
    };
    const {
      displayValue: locationName = '',
      normalize: {
        name: normalizeLocation = '',
        original_name: normalizeOrignialLocation = '',
      } = {},
    } = inputList.location;
    const { jobTitle, location } = payload;
    // 1. title  & location both found
    if (jobTitle.original_name && location) {
      jobTitle.original_name = _kebabCase(jobTitle.original_name);
      if (isValidExtendedLocation(location)) {
        remotePage = true;
        url = `/${jobTitle.original_name}-jobs/remote-jobs/`;
      } else {
        url = `/${jobTitle.original_name}-${location}`;
      }
      label = `${label}${jobTitle.original_name} And ${locationName}`;
      result.location = true;
      result.jobTitle = true;
    } else {
      // 2. title
      if (jobTitle.original_name) {
        jobTitle.original_name = _kebabCase(jobTitle.original_name);
        url = `/${jobTitle.original_name}`;
        label = `${label}${jobTitle.original_name}`;
        result.jobTitle = true;
      } else {
        // no title select check for normalize found
        const { displayValue = '', normalize: { name: normalizeJobTitle = '' } = {} } =
          inputList.jobTitle;
        if (displayValue && normalizeJobTitle.toLowerCase() === displayValue.toLowerCase()) {
          // normalize value found
          url = `/${normalizeJobTitle}`;
          label = `${label}${normalizeJobTitle}`;
          result.jobTitle = true;
        } else if (inputList.jobTitle?.displayValue?.length > 0) {
          mymatchesRedirect = true;
          url = `/matches/?jobSearch=${inputList.jobTitle.displayValue}`;
        }
      }
      if (location && !mymatchesRedirect) {
        if (isValidExtendedLocation(location)) {
          url = `/job-search/remote-jobs/`;
          remotePage = true;
        } else {
          url = url
            ? `${url}-${location.toLowerCase().split(' ').join('-')}`
            : `/${location.toLowerCase().split(' ').join('-')}`;
        }
        label = `${label}${locationName}`;
        result.location = true;
      } else if (!mymatchesRedirect) {
        /**
         * for location ex. san jose, ca ||  san jose ca
         * both will redirect to location san-jose-ca
         */
        if (
          locationName &&
          (locationName.toLowerCase() === normalizeLocation.toLowerCase() ||
            locationName.toLowerCase() === normalizeOrignialLocation.toLowerCase())
        ) {
          // get normalize location
          url = url ? `${url}-${normalizeLocation}` : `/${normalizeLocation}`;
          label = `${label}${locationName}`;
          result.location = true;
        }
      }
    }
    if (!mymatchesRedirect && !remotePage) url = url ? `${url}-jobs/` : '';
    if (!result.location && result.jobTitle && !mymatchesRedirect) url = url ? `${url}jobs/` : '';
    return { url, label, result };
  };

  redirectToNewUrl = (url) => {
    window.open(url, '_self');
    const timoutId = setTimeout(() => {
      this.setStateToCache();
      clearTimeout(timoutId);
    }, 0);
  };

  render() {
    const { user, inputFieldClasses } = this.props;
    const {
      inputList: {
        jobTitle: { displayValue: prefillTitleValue = '' },
        location: { displayValue: prefillLocationValue = '' },
      },
    } = this.state;
    const {
      handleChange,
      handleNormalizeZipCode,
      handleKeyDown,
      handleError,
      search,
      handleSearching,
      handleNormalize,
    } = this;
    const isUserLoggedIn = !!(user && user.isLoggedIn);
    return (
      <div
        role="tabpanel"
        className={classnames(
          'tab-pane active sitemapCareer-search-fieldset clearfix position-relative',
          sitemapClasses['sitemapCareer-search-fieldset'],
          stylesSearchBar.searchBarFieldset,
          { [stylesSearchBar.loggedOut]: !isUserLoggedIn }
        )}
        id="careers"
      >
        <form
          className={classnames('form-inline w-100', stylesSearchBar.formInline)}
          onSubmit={this.search}
        >
          <div className="form-group hi">
            <AutosuggestInput
              myRef={(instance) => {
                this.jobTitleRef = instance;
              }}
              category="jobTitle"
              prefillValue={prefillTitleValue}
              inputProps={{
                className: `typeahead form-control filterInput ${inputFieldClasses}`,
                id: 'searchJobTitleSearchBar',
                name: 'jobTitle',
              }}
              onChange={(inputName) => handleChange(inputName)}
              onKeyDown={handleKeyDown}
              onErrorCallback={(err) => handleError(err)}
              onNormalizeData={(suggestion) => handleNormalize('jobTitle', suggestion)}
              isSearching={handleSearching}
              className="w-100"
            />
          </div>
          <div className="form-group">
            <AutosuggestInput
              myRef={(instance) => {
                this.locationRef = instance;
              }}
              category="location"
              prefillValue={prefillLocationValue}
              inputProps={{
                className: `typeahead form-control filterInput ${inputFieldClasses}`,
                id: _uniqueId(),
                name: 'location',
              }}
              onChange={(inputName) => handleChange(inputName)}
              onNormalizeData={(suggestion) => handleNormalize('location', suggestion)}
              onEnterKeyDown={(e) => search(e)}
              onErrorCallback={(err) => handleError(err)}
              isSearching={handleSearching}
              extended
              className="w-100"
            />
          </div>
          <ZpButton
            theme="super-primary"
            size="big"
            type="submit"
            className="btn btn-orange"
            isFullWidth
          >
            <i className="fa fa-search" />
          </ZpButton>
        </form>
      </div>
    );
  }
}

const mapStateToProps = ({ user, locals }) => {
  return {
    user,
    locals,
  };
};

JobSearch.propTypes = propTypes;
JobSearch.defaultProps = defaultProps;

export default withTracking(connect(mapStateToProps)(JobSearch));
